import React, { useState, useEffect, Fragment } from 'react';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import moment from 'moment';

import TimeAndDate from './TimeAndDate';

import InputWrapper from '../../../common/InputWrapper';
import LabelQuestion from '../../../common/LabelQuestion';
import EditOrLock from '../common/EditOrLock';

import { showModal } from '../../../../actions/modal';

import { DELIVERY_TYPE_TRANSLATE, DELIVERY_SERVICES_TRANSLATE } from '../../../../config';

import { getDeliveryChannelName, getFBMDeliveryTitle } from '../../../../helpers';
import { useAutoTranslation } from '../../../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  padding-left: 0;
  padding-right: 0;
  box-shadow: none;
  border: 1px solid #ebebeb;
  > div {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-bottom: 8px;
    &:last-child {
      padding-bottom: 0;
    }
  }
  .logo-block {
    width: 100px;
    height: 30px;
    background-repeat: no-repeat;
    background-size: contain;
  }
  .delivery-services {
    display: flex;
    flex-wrap: wrap;
    > div {
      color: #929292;
      background-color: #ebebeb;
      font-size: 10px;
      border-radius: 10px;
      padding: 5px 7px;
      margin-right: 10px;
      margin-bottom: 10px;
      &:last-child {
        margin-right: 0;
      }
    }
  }
  .copy-tracking-number {
    position: relative;
    margin-top: -10px;
    .fe-copy {
      position: absolute;
      top: 20px;
      right: 37px;
      width: 12px;
      height: 14px;
      cursor: pointer;
    }
  }

  .takeout-top {
    margin-top: 8px;
  }

  .take-out-date {
    margin: 4px 0 0;
  }
  .card-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 2px;
    padding-bottom: 23px;
    h4 {
      font-size: 16px;
      margin: 0;
    }
  }

  .delivery-type-decr {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
`;

const DeliveryBlock = (props) => {
  const { sum, haveProducts, currentOrder, handleEditDelivery, actualizeOrder } = props;

  const { t } = useAutoTranslation();

  const dispatch = useDispatch();
  const [shouldOpenDeliveryModal, setShouldOpenDeliveryModal] = useState(false);

  useEffect(() => {
    if (shouldOpenDeliveryModal) {
      handleDeliveryModal(null, shouldOpenDeliveryModal);
      setShouldOpenDeliveryModal(false);
    }
  }, [shouldOpenDeliveryModal]);

  const { delivery } = currentOrder;
  const deliveryInfo = useSelector((state) => state.orders.deliveryInfo);
  const showServices = delivery && delivery.deliveryServices && delivery.deliveryServices.length;

  const handleDeliveryModal = (event, customDataForOrder) => {
    dispatch(
      showModal('EditOrderDelivery', {
        sum,
        currentOrder: customDataForOrder ? customDataForOrder : currentOrder,
        deliveryInfo,
        actualizeOrder,
        submitCallback: handleModalSubmit,
        setShouldOpenDeliveryModal,
      }),
    );
  };

  const definePickupPointId = (dataId) => {
    let pickupPointId = null;

    if (dataId) {
      pickupPointId = dataId.trim();
    } else if (currentOrder.delivery.pickupPointId) {
      pickupPointId = currentOrder.delivery.pickupPointId.trim();
    }

    return pickupPointId;
  };

  const handleModalSubmit = ({
    cityData,
    deliveryType,
    data,
    selectedServices,
    currentPointData,
    deliveryPayerType,
  }) => {
    return handleEditDelivery({
      plannedTakeOutDate:
        data.plannedTakeOutDate !== 'Invalid date' &&
        data?.plannedTakeOutDate?.length !== 0 &&
        data.plannedTakeOutDate !== undefined
          ? data.plannedTakeOutDate.length > 13
            ? data.plannedTakeOutDate
            : data.plannedTakeOutDate + ' 00:00:00'
          : null,
      delivery: {
        ...currentOrder.delivery,
        code: deliveryType.code,
        type: deliveryType.type,
        title: deliveryType.title,
        pickupPointId: definePickupPointId(data.pickupPointId),
        desiredDeliveryDate: data.desiredDeliveryDate || null,
        desiredDeliveryTimeFrom: data.desiredDeliveryTimeFrom || null,
        desiredDeliveryTimeTo: data.desiredDeliveryTimeTo || null,
        address: {
          ...currentOrder.delivery.address,
          country: cityData.country,
          countryCode: cityData.countryCode,
          region: cityData.region,
          area: cityData.area,
          city: cityData.city,
          settlement: cityData.settlement,
          fullAddress: cityData.fullAddress
            ? cityData.fullAddress
            : currentOrder.delivery.address.fullAddress,
          street:
            deliveryType.type === 'PVZ' && currentPointData?.address
              ? currentPointData.address.street
              : data.street,
          house:
            deliveryType.type === 'PVZ' && currentPointData?.address
              ? currentPointData.address.house
              : data.house,
          block:
            deliveryType.type === 'PVZ' && currentPointData?.address
              ? currentPointData.address.block
              : data.block,
          building:
            deliveryType.type === 'PVZ' && currentPointData?.address
              ? currentPointData.address.building
              : data.building,
          flat:
            deliveryType.type === 'PVZ' && currentPointData?.address
              ? currentPointData.address.flat
              : data.flat,
          floor:
            deliveryType.type === 'PVZ' && currentPointData?.address
              ? currentPointData.address.floor
              : data.floor,
          zip:
            deliveryType.type === 'PVZ' && currentPointData?.address
              ? currentPointData.address.zip
              : data.zip,
          regionFias: cityData.regionFias,
          areaFias: cityData.areaFias,
          cityFias: cityData.cityFias,
          settlementFias: cityData.settlementFias,
        },
        deliveryServices: selectedServices.length ? selectedServices : null,
        deliveryPayerType,
      },
    });
  };

  const findCurrentDeliveryInfo = () =>
    deliveryInfo?.find((item) => item.code === currentOrder.delivery?.code);

  const isDeliveryByCactus = () => {
    const { fbmType, fboDeliveryByCactus } = findCurrentDeliveryInfo();
    return fbmType && fboDeliveryByCactus;
  };

  const getProductDeliveryType = () => {
    const { product } = findCurrentDeliveryInfo();
    return product ? <span>{product}</span> : null;
  };

  const getDeliveryTitle = () => {
    const { title } = findCurrentDeliveryInfo();
    return title ? <span>({title})</span> : null;
  };

  const isFbmType = () => {
    const delivery = findCurrentDeliveryInfo();
    return delivery?.fbmType ? true : false;
  };

  return (
    <Wrapper
      className={`card-box ${!(haveProducts && delivery && delivery.code) ? 'disabled' : ''}`}
    >
      <div className="card-title">
        <h4 className="header-title">{t('Доставка')}</h4>
        {haveProducts && delivery ? (
          <EditOrLock
            permissions={currentOrder.permissionList}
            permissionKey="DELIVERY_EDITABLE"
            action={handleDeliveryModal}
          />
        ) : null}
      </div>
      {haveProducts && delivery ? (
        <Fragment>
          {delivery.code ? (
            <Fragment>
              <div className="border-top" style={{ paddingTop: '24px', marginBottom: '13px' }}>
                <div className="block-subtitle" style={{ marginBottom: '3px' }}>
                  {t('ТИП ДОСТАВКИ')}
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {delivery.logoUrl ? (
                    <div
                      className="logo-block"
                      style={{ backgroundImage: `url(${delivery.logoUrl || null})` }}
                    />
                  ) : (
                    <div>{delivery.title}</div>
                  )}
                </div>

                {getFBMDeliveryTitle(delivery, deliveryInfo) ? (
                  <div className="delivery-type-decr">
                    {getFBMDeliveryTitle(delivery, deliveryInfo)}
                    {getDeliveryTitle()}
                  </div>
                ) : (
                  <div className="delivery-type-decr">
                    {getProductDeliveryType()}
                    {getDeliveryTitle()}
                  </div>
                )}

                {delivery.type !== 'SELF_PICKUP' ? (
                  <div>{DELIVERY_TYPE_TRANSLATE[delivery.type]}</div>
                ) : null}
                {(delivery.type === 'PVZ' || delivery.type === 'SELF_PICKUP') &&
                delivery.pickupPointId ? (
                  <div style={{ marginTop: '3px', display: 'flex', alignItems: 'center' }}>
                    Код:
                    <span style={{ color: '#f1556c', fontWeight: 500, marginLeft: '5px' }}>
                      {delivery.pickupPointId}
                    </span>
                    {delivery.pvzComment || delivery.deliveryComment ? (
                      <div>
                        <LabelQuestion
                          icon="fe-info"
                          question={delivery.pvzComment || delivery.deliveryComment}
                          style={{ fontSize: '16px', marginBottom: '-3px' }}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {delivery.actualDeliveryDate ? (
                <Fragment>
                  <div className="mt-2">
                    <div className="block-subtitle">{t('Дата доставки')}</div>
                  </div>
                  <div>
                    {moment(delivery.actualDeliveryDate, 'YYYY-MM-DD').format('DD.MM.YYYY')}
                  </div>
                </Fragment>
              ) : null}
            </Fragment>
          ) : (
            <div className="blocked-block border-top">
              <div className="change-link" onClick={handleDeliveryModal}>
                {t('Выбрать способ доставки')}
              </div>
            </div>
          )}
          {delivery.address && delivery.address.fullAddress ? (
            <Fragment>
              <div className="mt-2">
                <div className="block-subtitle">{t('Адрес доставки')}</div>
              </div>
              <div className="card-content">
                {/* <div style={{fontWeight:'bold'}}>{delivery.address.city}</div>
                                <div>{delivery.address.region}</div>
                                <div></div> */}
                {delivery.type !== 'SELF_PICKUP' || isDeliveryByCactus() ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div className="mr-2">{delivery.address.fullAddress}</div>
                    <i
                      className="fe-map-pin ml-auto"
                      style={{
                        color:
                          !currentOrder.delivery.address.cityFias &&
                          !currentOrder.delivery.address.settlementFias
                            ? '#f1556c'
                            : '#62cab4',
                        fontSize: '18px',
                        marginTop: '-3px',
                      }}
                    />
                  </div>
                ) : (
                  <div>{t('Без адреса. Выдача заказа покупателю со склада.')}</div>
                )}
              </div>
            </Fragment>
          ) : null}

          {delivery.code ? (
            <Fragment>
              {delivery.trackingNumber ? (
                <Fragment>
                  <div className="mt-2" style={{ display: 'flex', alignItems: 'center' }}>
                    <div className="block-subtitle">{t('Номер трекинга')}</div>
                    {delivery.trackingURL ? (
                      <div className="change-link change-link--gray" style={{ marginLeft: 'auto' }}>
                        <a
                          href={delivery.trackingURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#72747b' }}
                        >
                          {t('Отследить')}
                        </a>
                      </div>
                    ) : null}
                  </div>
                  <div className="copy-tracking-number card-content">
                    <InputWrapper value={delivery.trackingNumber} readOnly />
                    <CopyToClipboard
                      text={delivery.trackingNumber}
                      onCopy={() => toast.info('Номер трекинга скопирован')}
                    >
                      <i className="fe-copy" />
                    </CopyToClipboard>
                  </div>
                </Fragment>
              ) : null}

              {showServices ? (
                <Fragment>
                  <div className="mt-2">
                    <div className="block-subtitle">{t('Дополнительные опции')}</div>
                  </div>
                  <div className="delivery-services">
                    {delivery.deliveryServices.map((s) => (
                      <div>{DELIVERY_SERVICES_TRANSLATE[s] || s}</div>
                    ))}
                  </div>
                </Fragment>
              ) : null}
            </Fragment>
          ) : null}

          {currentOrder.delivery.deliveryCancelReason && (
            <div className="takeout-top">
              <div className="block-subtitle">{t('Причина отмены доставки')}</div>
              <p className="take-out-date">{currentOrder.delivery.deliveryCancelReason}</p>
            </div>
          )}

          {currentOrder.isMarketplaceOrder && (
            <div className="border-top takeout-top">
              <div className="block-subtitle">{t('Планируемая дата отгрузки')}</div>
              <p className="take-out-date">{currentOrder.plannedTakeOutDate}</p>
            </div>
          )}
        </Fragment>
      ) : (
        <div className="blocked-block border-top">
          <div className="block-subtitle">{t('Изменения не доступны')}</div>
        </div>
      )}

      <TimeAndDate delivery={delivery} currentOrder={currentOrder} isFbmType={isFbmType()} />
    </Wrapper>
  );
};

export default DeliveryBlock;
